import React, {
    useCallback, useState, useMemo, useContext, useRef,
} from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import cn from 'classnames';

import apolloClient from 'app/apollo/client';
import { Desktop } from 'app/components/Responsive';
import { isDesktop } from 'app/utils/resolution';

import { DishDetailsContext } from '../../DishDetailsContainer/DishDeltailsContextProvider';

import { DishPicture } from '../DishPicture';
import { DishPictureSlide } from './DishPictureSlide';
import { DishSlide } from './DishSlide';
import { SliderHoverControls } from './SliderHoverControls';
import { SliderArrowControls } from './SliderArrowControls/SliderArrowControls';
import { SliderDots } from './SliderDots';

import { DISH_IMAGES_FRAGMENT } from './graphql/DishMediaContentOperation.graphql';

import s from './dish-media-content.module.scss';
import ds from '../../DishDetails/dish-details.scss';


const sliderSettings = {
    mobileFirst: true,
    arrows: false,
    dots: false,
    // easing: 'ease-in-out',
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
};


const DishPictureWithSlider = ({
    context,
    slides, alt, imageUrlDesktop, imageUrl, id,
}) => {
    const {
        handleOpenDetails,
        activeDetailsSlide,
    } = useContext(DishDetailsContext);

    const preparedSlides = useMemo(() => {
        const mapped = slides.map((slide) => {
            const { id: slideId } = slide;

            if (slideId) {
                const data = apolloClient.readFragment(
                    {
                        id: `menuDish:${slideId}`,
                        fragment: DISH_IMAGES_FRAGMENT,
                    },
                );

                if (!data) {
                    return false;
                }

                return {
                    ...slide,
                    imageUrl: data.previewMobileImage,
                    imageUrlDesktop: data.previewImage,
                    alt: data.title,
                };
            }

            return slide;
        });

        return mapped.filter((slide) => slide);
    }, [slides]);

    const initialSlide = useMemo(() => {
        if (context !== 'details' || !activeDetailsSlide) return 0;

        const { type, src, imageUrl: img } = activeDetailsSlide;

        const preparedSlideIndex = preparedSlides.findIndex(
            (sl) => sl.type === type && sl.src === src && sl.imageUrl === img,
        );

        if (preparedSlideIndex > -1) {
            return preparedSlideIndex + 1;
        }

        return 0;
    }, [context, activeDetailsSlide, preparedSlides]);

    const onClickOpenDetails = useCallback((slide) => {
        handleOpenDetails(id, { slide });
    }, [id, handleOpenDetails]);


    const [currentSlideIndex, setCurrentSlideIndex] = useState(initialSlide);

    const handleSlideChanged = useCallback((prevIndex, currentIndex) => {
        setCurrentSlideIndex(currentIndex);
    }, []);

    const sliderRef = useRef(null);

    const handleHoverSlideArea = useCallback((index) => {
        const slider = sliderRef.current;
        if (slider) {
            slider.slickGoTo(index, true);
        }
    }, []);

    const handleClickSlideArrow = useCallback((direction) => {
        const slider = sliderRef.current;
        if (!slider) return;

        if (direction === 'next') {
            slider.slickNext();
        } else if (direction === 'prev') {
            slider.slickPrev();
        }
    }, []);


    const infinite = context === 'details' ? true : !isDesktop();

    return (
        <>
            <Slider
                {...sliderSettings}
                infinite={infinite}
                initialSlide={initialSlide}
                beforeChange={handleSlideChanged}
                ref={sliderRef}
            >
                {/* MAIN PICTURE */}
                <div className="promo-checkout-data-slide-container">
                    <div className={s.slideRelativeWrapper}>
                        <DishPictureSlide
                            context={context}
                            alt={alt}
                            imageUrl={imageUrl}
                            imageUrlDesktop={imageUrlDesktop}
                            onClickOpenDetails={onClickOpenDetails}
                        />
                    </div>
                </div>

                {/* SLIDER PICTURES */}
                {preparedSlides.map((slide, index) => {
                    const key = slide.src || slide.imageUrl;
                    const autoPlay = initialSlide > 0 && initialSlide === index + 1;

                    return (
                        <DishSlide
                            key={key}
                            id={id}
                            context={context}
                            currentSlideIndex={currentSlideIndex}
                            index={index}
                            slide={slide}
                            onClickOpenDetails={onClickOpenDetails}
                            autoPlay={autoPlay}
                        />
                    );
                })}
            </Slider>

            <SliderDots slidesCount={preparedSlides.length + 1} currentSlideIndex={currentSlideIndex} />

            <Desktop>
                {context === 'card' ? (
                    <SliderHoverControls
                        slides={preparedSlides}
                        handleHoverSlideArea={handleHoverSlideArea}
                        onClickOpenDetails={context === 'card' ? onClickOpenDetails : undefined}
                    />
                ) : (
                    <SliderArrowControls
                        handleClickSlideArrow={handleClickSlideArrow}
                    />
                )}
            </Desktop>
        </>
    );
};

DishPictureWithSlider.propTypes = {
    context: PropTypes.oneOf(['details', 'card']).isRequired,
    id: PropTypes.string.isRequired,
    slides: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.oneOf(['img', 'video']).isRequired,
        imageUrl: PropTypes.string,
        imageUrlDesktop: PropTypes.string,
        src: PropTypes.string,
        alt: PropTypes.string,
    })).isRequired,
    alt: PropTypes.string.isRequired,
    imageUrl: PropTypes.string.isRequired,
    imageUrlDesktop: PropTypes.string.isRequired,
};


const emptySlides = [];
const certificateDishes = [
    '3889',
    '3890',
];

const certificateDishesImages = {
    3889: require('../../../../pages/_disabled/blyuda-dlya-novogodnego-stola_25/dishesData/giftImages/detail_3000.jpg'),
    3890: require('../../../../pages/_disabled/blyuda-dlya-novogodnego-stola_25/dishesData/giftImages/detail_5000.jpg'),
};

export const DishMediaContent = ({
    context = 'card',
    useLazyLoading = false,
    alt = '',
    imageUrl,
    imageUrlDesktop,
    slides = emptySlides,
    additionalContent = null,
    id,
}) => {
    const rootClassName = context === 'details' ? ds['ny__details-image__root'] : 'ny__dish__image-container';

    const slickSliderContainerClasses = cn({
        'slick-slider-container': true,
        'slick-slider-height-container': true,
    });

    const currentImage = imageUrl;
    const currentImageDesktop = imageUrlDesktop;

    return (
        <div className={rootClassName}>
            <div className={slickSliderContainerClasses}>
                {slides.length > 0 ? (
                    <DishPictureWithSlider
                        slides={slides}
                        alt={alt}
                        imageUrl={currentImage}
                        imageUrlDesktop={currentImageDesktop}
                        id={id}
                        context={context}
                    />
                ) : (
                    <DishPicture
                        useLazyLoading={useLazyLoading}
                        alt={alt}
                        imageUrl={currentImage}
                        imageUrlDesktop={currentImageDesktop}
                        id={id}
                    />
                )}
                {additionalContent}
            </div>
        </div>
    );
};

DishMediaContent.propTypes = {
    context: PropTypes.oneOf(['details', 'card']),
    id: PropTypes.string.isRequired,
    useLazyLoading: PropTypes.bool,
    alt: PropTypes.string,
    imageUrl: PropTypes.string.isRequired,
    imageUrlDesktop: PropTypes.string.isRequired,
    slides: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.oneOf(['img', 'video']).isRequired,
        imageUrl: PropTypes.string,
        imageUrlDesktop: PropTypes.string,
        src: PropTypes.string,
        alt: PropTypes.string,
    })),
    additionalContent: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
};
